import { ChangeEvent, DragEvent, KeyboardEvent, MouseEvent, useRef, useState } from "react";
import Dialog from "@rio-cloud/rio-uikit/Dialog";
import { Button, Notification } from "@rio-cloud/rio-uikit";
import { useT } from "@hooks";
import type { TransactionImportType } from "@types";
import ImportProgress from "./ImportProgress";
import type { TransactionImportState } from "./importState";

const ACCEPTED_EXTENSIONS = [".xlsx", ".xls"];

export const MAX_SPREADSHEET_SIZE_BYTES = 10 * 1024 * 1024;

const CARD_CLASS =
  "display-flex flex-column align-items-center justify-content-center gap-5 padding-20 border rounded-large min-height-150 text-center";

export const isAcceptedSpreadsheet = (file: File) =>
  ACCEPTED_EXTENSIONS.some((extension) => file.name.toLowerCase().endsWith(extension));

interface Props {
  show: boolean;
  title: string;
  manualTitle: string;
  spreadsheetTitle: string;
  onClose: () => void;
  onManualEntry: () => void;
  onDownloadTemplate?: () => void;
  onSpreadsheetContinue?: (file: File) => void;
  importType?: TransactionImportType;
  importState?: TransactionImportState;
  onImportConclude?: () => void;
}

const TransactionEntryDialog = ({
  show,
  title,
  manualTitle,
  spreadsheetTitle,
  onClose,
  onManualEntry,
  onDownloadTemplate,
  onSpreadsheetContinue,
  importType = "DESPESA",
  importState,
  onImportConclude,
}: Props) => {
  const t = useT();
  const inputRef = useRef<HTMLInputElement>(null);
  const [spreadsheet, setSpreadsheet] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const selectSpreadsheet = (file?: File | null) => {
    if (!file) return;

    if (!isAcceptedSpreadsheet(file)) {
      Notification.error(t("transactionEntry.spreadsheet.invalidType"));
      return;
    }

    if (file.size > MAX_SPREADSHEET_SIZE_BYTES) {
      Notification.error(t("transactionEntry.spreadsheet.tooLarge"));
      return;
    }

    setSpreadsheet(file);
  };

  const clearInput = () => {
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleClose = () => {
    setSpreadsheet(null);
    setIsDragging(false);
    clearInput();
    onClose();
  };

  const handleManualEntry = () => {
    setSpreadsheet(null);
    setIsDragging(false);
    clearInput();
    onManualEntry();
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!spreadsheet) setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (spreadsheet) return;
    selectSpreadsheet(event.dataTransfer?.files?.[0]);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    selectSpreadsheet(event.target.files?.[0]);
    clearInput();
  };

  const openFilePicker = () => {
    if (!spreadsheet) inputRef.current?.click();
  };

  const handleDropzoneKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    openFilePicker();
  };

  const stopPropagation = (event: MouseEvent) => event.stopPropagation();

  const handleRemove = (event: MouseEvent) => {
    event.stopPropagation();
    setSpreadsheet(null);
    clearInput();
  };

  const dropzoneBorder = spreadsheet ? "" : "border-style-dashed";
  const dropzoneColor = isDragging ? "border-color-primary" : "";
  const dropzoneCursor = spreadsheet ? "" : "cursor-pointer";

  const isImporting = !!importState && importState.phase !== "idle";
  const canConclude = isImporting && (importState.phase === "finished" || importState.phase === "rejected");

  return (
    <Dialog
      show={show}
      title={<span className="text-medium text-size-16">{title}</span>}
      body={
        isImporting ? (
          <ImportProgress state={importState} type={importType} />
        ) : (
          <div className="display-grid grid-cols-1 grid-cols-2-sm gap-20">
            <div className={CARD_CLASS} data-testid="transaction-entry-manual">
              <span className="rioglyph rioglyph-document text-size-20" aria-hidden="true" />
              <span className="text-size-16 margin-top-5">{manualTitle}</span>
              <Button bsStyle={Button.PRIMARY} className="rounded-small margin-top-10" onClick={handleManualEntry}>
                {t("transactionEntry.action.continue")}
              </Button>
            </div>

            <div
              className={`${CARD_CLASS} ${dropzoneBorder} ${dropzoneColor} ${dropzoneCursor}`.trim()}
              data-testid="transaction-entry-spreadsheet"
              role={spreadsheet ? undefined : "button"}
              tabIndex={spreadsheet ? undefined : 0}
              aria-label={spreadsheet ? undefined : t("transactionEntry.spreadsheet.hint")}
              onClick={openFilePicker}
              onKeyDown={handleDropzoneKeyDown}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                ref={inputRef}
                type="file"
                accept={ACCEPTED_EXTENSIONS.join(",")}
                className="display-none"
                data-testid="transaction-entry-spreadsheet-input"
                onClick={stopPropagation}
                onChange={handleInputChange}
              />
              <span className="rioglyph rioglyph-th-list text-size-20" aria-hidden="true" />
              <span className="text-size-16 margin-top-5">{spreadsheetTitle}</span>

              {spreadsheet ? (
                <>
                  <span className="text-size-12 word-break-all">
                    <span className="text-color-primary" data-testid="transaction-entry-spreadsheet-name">
                      {spreadsheet.name}
                    </span>{" "}
                    <button
                      type="button"
                      className="btn btn-link padding-0 text-size-12 text-color-danger text-decoration-underline"
                      onClick={handleRemove}
                    >
                      {t("common.action.remove")}
                    </button>
                  </span>
                  <Button
                    bsStyle={Button.PRIMARY}
                    className="rounded-small margin-top-10"
                    onClick={(event: MouseEvent) => {
                      event.stopPropagation();
                      onSpreadsheetContinue?.(spreadsheet);
                      setSpreadsheet(null);
                      clearInput();
                    }}
                  >
                    {t("transactionEntry.action.continue")}
                  </Button>
                </>
              ) : (
                <>
                  <span className="text-size-12 text-color-gray">{t("transactionEntry.spreadsheet.hint")}</span>
                  <Button
                    bsStyle={Button.PRIMARY}
                    variant={Button.VARIANT_OUTLINE}
                    className="rounded-small margin-top-10"
                    onClick={(event: MouseEvent) => {
                      stopPropagation(event);
                      onDownloadTemplate?.();
                    }}
                  >
                    {t("transactionEntry.spreadsheet.downloadTemplate")}
                  </Button>
                </>
              )}
            </div>
          </div>
        )
      }
      footer={
        canConclude ? (
          <div className="display-flex justify-content-end">
            <Button bsStyle={Button.PRIMARY} className="rounded-small" onClick={onImportConclude}>
              {t("transactionEntry.import.action.conclude")}
            </Button>
          </div>
        ) : undefined
      }
      onClose={handleClose}
      showCloseButton
      bsSize="md"
      className="display-flex align-items-center"
    />
  );
};

export default TransactionEntryDialog;
